<template>
  <div class="content">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="content-heading mb-0">
        <i class="fa fa-info-circle mr-2"></i>
        Детали импорта #{{ importId }}
      </h2>
      <button class="btn btn-secondary" @click="goBack">
        <i class="fa fa-arrow-left"></i> Назад
      </button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fa-3x"></i>
      <p class="mt-3">Загрузка...</p>
    </div>

    <!-- Content -->
    <div v-else-if="importData">
      <!-- Import Info -->
      <div class="block block-rounded">
        <div class="block-header block-header-default">
          <h3 class="block-title">Информация об импорте</h3>
        </div>
        <div class="block-content compact-info">
          <div class="row">
            <div class="col-md-6">
              <table class="table table-borderless">
                <tr>
                  <td class="font-weight-bold">Дата цессии:</td>
                  <td>{{ importData.request_date }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">БИН страхователя:</td>
                  <td>{{ importData.request_bin }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">БИН коллектора:</td>
                  <td>{{ importData.request_collector_bin }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Тип импорта:</td>
                  <td>
                    <span class="badge badge-info">
                      {{ importTypeText }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Статус:</td>
                  <td>
                    <span
                      class="badge"
                      :class="getStatusClass(importData.status)"
                    >
                      {{ getStatusText(importData.status) }}
                    </span>
                  </td>
                </tr>
              </table>
            </div>
            <div class="col-md-6">
              <table class="table table-borderless">
                <tr>
                  <td class="font-weight-bold">Всего записей:</td>
                  <td>{{ importData.total_records }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Обработано:</td>
                  <td>{{ importData.processed_count }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Создано:</td>
                  <td class="text-success">{{ importData.created_count }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Обновлено:</td>
                  <td class="text-info">{{ importData.updated_count }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Ошибок:</td>
                  <td class="text-danger">{{ importData.failed_count }}</td>
                </tr>
              </table>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <table class="table table-borderless">
                <tr>
                  <td class="font-weight-bold">Начало:</td>
                  <td>{{ formatDate(importData.started_at) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Завершение:</td>
                  <td>{{ formatDate(importData.completed_at) }}</td>
                </tr>
                <tr v-if="importData.triggered_by">
                  <td class="font-weight-bold">Запущено пользователем:</td>
                  <td>{{ getUserFullName(importData.triggered_by) }}</td>
                </tr>
              </table>
            </div>
          </div>

          <div v-if="importData.errors" class="alert alert-danger mt-2 mb-0">
            <strong>Ошибка:</strong> {{ importData.errors }}
          </div>
        </div>
      </div>

      <!-- Records -->
      <div class="block block-rounded">
        <div class="block-header block-header-default">
          <h3 class="block-title">
            Детальные записи
            <span class="badge badge-secondary ml-2">{{ pagination.total }}</span>
          </h3>
          <div class="block-options">
            <button
              class="btn btn-sm btn-success"
              @click="exportRecords"
              :disabled="isExporting"
            >
              <i class="fa fa-file-excel" v-if="!isExporting"></i>
              <i class="fa fa-spinner fa-spin" v-else></i>
              {{ isExporting ? 'Экспорт...' : 'Экспорт в Excel' }}
            </button>
          </div>
        </div>
        <div class="block-content">
          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-4">
              <select
                class="form-control"
                v-model="filters.status"
                @change="fetchRecords(1)"
              >
                <option value="">Все статусы</option>
                <option value="pending">Ожидание</option>
                <option value="success">Успешно</option>
                <option value="failed">Ошибка</option>
                <option value="skipped">Пропущено</option>
              </select>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-striped table-vcenter">
              <thead>
                <tr>
                  <th>ИИН должника</th>
                  <th>Номер договора</th>
                  <th>Статус</th>
                  <th>Действие</th>
                  <th>ФИО должника</th>
                  <th v-if="isPaymentsImport">Платежей</th>
                  <th v-if="isPaymentsImport">Сумма платежей</th>
                  <th>Ошибка</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingRecords">
                  <td :colspan="isPaymentsImport ? 8 : 6" class="text-center">
                    <i class="fa fa-spinner fa-spin"></i> Загрузка...
                  </td>
                </tr>
                <tr v-else-if="records.length === 0">
                  <td :colspan="isPaymentsImport ? 8 : 6" class="text-center text-muted">
                    Нет записей
                  </td>
                </tr>
                <template v-else>
                  <template v-for="record in records">
                    <tr :key="record.id">
                      <td>{{ record.debtor_iin }}</td>
                      <td>{{ record.debt_number }}</td>
                      <td>
                        <span
                          class="badge badge-sm"
                          :class="getRecordStatusClass(record.status)"
                        >
                          {{ getRecordStatusText(record.status) }}
                        </span>
                      </td>
                      <td>{{ getRecordActionText(record.action) }}</td>
                      <td>
                        <span v-if="record.debtor">{{ record.debtor.fullname }}</span>
                        <span v-else-if="record.debt && record.debt.debtor">{{ record.debt.debtor.fullname }}</span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td v-if="isPaymentsImport">
                        <span v-if="record.payments && record.payments.length > 0">
                          <button
                            class="btn btn-sm btn-link p-0"
                            @click="togglePayments(record.id)"
                            :title="expandedPayments[record.id] ? 'Скрыть платежи' : 'Показать платежи'"
                          >
                            <span class="badge badge-info">{{ record.payments_count || record.payments.length }}</span>
                          </button>
                        </span>
                        <span v-else class="text-muted">0</span>
                      </td>
                      <td v-if="isPaymentsImport">
                        <span v-if="record.payments_total_amount">
                          {{ formatAmount(record.payments_total_amount) }}
                        </span>
                        <span v-else-if="record.payments && record.payments.length > 0">
                          {{ formatAmount(record.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <small class="text-danger">
                          {{ record.error_message || '-' }}
                        </small>
                      </td>
                    </tr>
                    <!-- Expanded payments row -->
                    <tr
                      v-if="isPaymentsImport && expandedPayments[record.id] && record.payments && record.payments.length > 0"
                      class="bg-light"
                    >
                      <td :colspan="isPaymentsImport ? 8 : 6">
                        <div class="p-3">
                          <h6 class="mb-2">
                            <i class="fa fa-money-bill-wave mr-2"></i>
                            Платежи по договору {{ record.debt_number }}
                          </h6>
                          <div class="table-responsive">
                            <table class="table table-sm table-bordered mb-0">
                              <thead>
                                <tr>
                                  <th>Дата платежа</th>
                                  <th>Сумма</th>
                                  <th>Номер квитанции</th>
                                  <th>Дата квитанции</th>
                                  <th>Статус</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="payment in record.payments" :key="payment.id">
                                  <td>{{ payment.payment_date }}</td>
                                  <td class="font-weight-bold">{{ formatAmount(payment.amount) }}</td>
                                  <td>{{ payment.receipt_number || '-' }}</td>
                                  <td>{{ payment.date_receipt || '-' }}</td>
                                  <td>
                                    <span
                                      class="badge badge-sm"
                                      :class="payment.confirmed ? 'badge-success' : 'badge-warning'"
                                    >
                                      {{ payment.confirmed ? 'Подтверждён' : 'Не подтвержден' }}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td class="font-weight-bold">Итого:</td>
                                  <td class="font-weight-bold">
                                    {{ formatAmount(record.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)) }}
                                  </td>
                                  <td colspan="3"></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.total > pagination.per_page" class="row">
            <div class="col-md-12">
              <nav>
                <ul class="pagination justify-content-center">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.current_page === 1 }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="changePage(pagination.current_page - 1)"
                    >
                      Предыдущая
                    </a>
                  </li>
                  <li
                    v-for="page in visiblePages"
                    :key="page"
                    class="page-item"
                    :class="{ active: pagination.current_page === page }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="changePage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{
                      disabled:
                        pagination.current_page === pagination.last_page,
                    }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="changePage(pagination.current_page + 1)"
                    >
                      Следующая
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else class="alert alert-danger">
      <i class="fa fa-exclamation-circle"></i>
      Не удалось загрузить данные импорта
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FFinsImportDetail',
  data() {
    return {
      importId: null,
      importData: null,
      records: [],
      isLoading: true,
      loadingRecords: false,
      isExporting: false,
      importType: 'debts', // 'debts' or 'payments'
      expandedPayments: {}, // Track which payment records are expanded
      filters: {
        status: '',
      },
      pagination: {
        current_page: 1,
        per_page: 50,
        total: 0,
        last_page: 1,
      },
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      const delta = 2;

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
      ) {
        pages.push(i);
      }

      if (current - delta > 2) {
        pages.unshift('...');
      }
      if (current + delta < last - 1) {
        pages.push('...');
      }

      pages.unshift(1);
      if (last > 1) {
        pages.push(last);
      }

      return pages;
    },
    isPaymentsImport() {
      return this.importType === 'payments';
    },
    importTypeText() {
      return this.importType === 'payments' ? 'Платежи' : 'Договоры';
    },
  },
  mounted() {
    this.importId = this.$route.params.id;
    this.fetchImportData();
    this.fetchRecords();
  },
  methods: {
    async fetchImportData() {
      this.isLoading = true;

      try {
        const response = await axios.get(`/ffins/imports/${this.importId}`);

        if (response.data.success) {
          this.importData = response.data.data;

          // Если API прислал import_type или type, используем это для отображения
          const apiType =
            this.importData.import_type || this.importData.type || null;
          if (apiType) {
            this.importType = apiType;
          }
        }
      } catch (error) {
        console.error('Error fetching import data:', error);
        window.Swal({
          title: 'Ошибка!',
          text: 'Не удалось загрузить данные импорта',
          icon: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchRecords(page = 1) {
      this.loadingRecords = true;

      try {
        const response = await axios.get(
          `/ffins/imports/${this.importId}/records`,
          {
            params: {
              page,
              per_page: this.pagination.per_page,
              status: this.filters.status || undefined,
            },
          }
        );

        if (response.data.success) {
          this.records = response.data.data.data;
          this.pagination = {
            current_page: response.data.data.current_page,
            per_page: response.data.data.per_page,
            total: response.data.data.total,
            last_page: response.data.data.last_page,
          };
          
          // Определяем тип импорта из метаданных
          if (response.data.meta && response.data.meta.import_type) {
            this.importType = response.data.meta.import_type;
          }
        }
      } catch (error) {
        console.error('Error fetching records:', error);
      } finally {
        this.loadingRecords = false;
      }
    },
    async exportRecords() {
      this.isExporting = true;

      try {
        const response = await axios.get(
          `/ffins/imports/${this.importId}/export`,
          {
            responseType: 'blob',
          }
        );

        // Создаем ссылку для скачивания
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `ffins_import_${this.importId}_records_${new Date().getTime()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.Swal({
          title: 'Успех!',
          text: 'Экспорт успешно завершен',
          icon: 'success',
          timer: 2000,
        });
      } catch (error) {
        console.error('Error exporting records:', error);
        window.Swal({
          title: 'Ошибка!',
          text: 'Не удалось выполнить экспорт',
          icon: 'error',
        });
      } finally {
        this.isExporting = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.fetchRecords(page);
      }
    },
    goBack() {
      this.$router.push({ name: 'adminFFins' });
    },
    getStatusClass(status) {
      const classes = {
        pending: 'badge-secondary',
        processing: 'badge-info',
        completed: 'badge-success',
        failed: 'badge-danger',
      };
      return classes[status] || 'badge-secondary';
    },
    getStatusText(status) {
      const texts = {
        pending: 'Ожидание',
        processing: 'В процессе',
        completed: 'Завершено',
        failed: 'Ошибка',
      };
      return texts[status] || status;
    },
    getRecordStatusClass(status) {
      const classes = {
        pending: 'badge-secondary',
        success: 'badge-success',
        failed: 'badge-danger',
        skipped: 'badge-warning',
      };
      return classes[status] || 'badge-secondary';
    },
    getRecordStatusText(status) {
      const texts = {
        pending: 'Ожидание',
        success: 'Успешно',
        failed: 'Ошибка',
        skipped: 'Пропущено',
      };
      return texts[status] || status;
    },
    getRecordActionText(action) {
      const texts = {
        created: 'Создан',
        updated: 'Обновлен',
      };
      return texts[action] || (action ? action : '-');
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('ru-RU');
    },
    getUserFullName(user) {
      if (!user) return '-';
      return `${user.lastname || ''} ${user.firstname || ''} ${user.middlename || ''}`.trim();
    },
    formatAmount(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    togglePayments(recordId) {
      this.$set(this.expandedPayments, recordId, !this.expandedPayments[recordId]);
    },
  },
};
</script>

<style scoped>
.block {
  margin-bottom: 20px;
}

.compact-info {
  padding: 0.75rem 1.25rem;
}

.compact-info .row {
  margin-bottom: 0;
  margin-left: -5px;
  margin-right: -5px;
}

.compact-info .row + .row {
  margin-top: 0;
}

.compact-info [class*="col-"] {
  padding-left: 5px;
  padding-right: 5px;
}

.table-borderless {
  margin-bottom: 0;
}

.table-borderless td,
.table-borderless th {
  padding: 0.15rem 0.5rem;
  line-height: 1.3;
  border: none;
  vertical-align: middle;
}

.table-borderless tr {
  line-height: 1.3;
}

.table-borderless td:first-child {
  width: 180px;
  white-space: nowrap;
  color: #495057;
  font-size: 0.95rem;
}

.table-borderless td:last-child {
  color: #000;
  font-size: 0.95rem;
}

.alert {
  padding: 0.5rem 1rem;
  font-size: 0.95rem;
}
</style>
